/**
 * 会計四半期 (7月始まり) と Qまとめシート名の相互変換。
 *
 * バックエンド `combat-power/infrastructure/quarter-resolver.ts` と同一ロジックをフロントで
 * 再現する。月間pt カードの月プルダウンを「availableQuarters 由来の全過去月」から組み立て、
 * 選択月が属する Q (フェッチ対象) を解決するために使う。
 * 会計年度は 7月始まり / 6月終わり、年度番号は終了年に揃える (2026/7-9 = FY2027 Q1)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 会計四半期 (7月始まり) と Qまとめシート名・構成月の相互変換
 * @graph-connects none
 */

/** 会計四半期。fy は終了年に揃える (2026/7-9 = FY2027 Q1)。 @graph-connects none */
export interface FiscalQuarter {
  fy: number;
  q: 1 | 2 | 3 | 4;
}

/** "{fy}_{q}Q[まとめ]" を FiscalQuarter へ。非該当は null。 @graph-connects none */
export function parseQuarterTitle(title: string): FiscalQuarter | null {
  const m = /^(\d{4})_([1-4])Q/.exec(title);
  if (m === null) return null;
  return { fy: parseInt(m[1], 10), q: parseInt(m[2], 10) as 1 | 2 | 3 | 4 };
}

/**
 * 暦年・月(1-12) が属する会計四半期を返す。
 * 7-9月=Q1, 10-12月=Q2, 1-3月=Q3, 4-6月=Q4。年度番号は終了年 (2026/7 = FY2027 Q1)。
 * @graph-connects none
 */
export function fiscalQuarterOf(year: number, month: number): FiscalQuarter {
  if (month >= 7 && month <= 9) return { fy: year + 1, q: 1 };
  if (month >= 10 && month <= 12) return { fy: year + 1, q: 2 };
  if (month >= 1 && month <= 3) return { fy: year, q: 3 };
  return { fy: year, q: 4 };
}

/** FiscalQuarter → 構成 3 ヶ月 "YYYY-MM" (昇順)。 @graph-connects none */
export function quarterMonths(q: FiscalQuarter): string[] {
  const startYear = q.q >= 3 ? q.fy : q.fy - 1;
  const startMonth = ({ 1: 7, 2: 10, 3: 1, 4: 4 } as const)[q.q];
  const months: string[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(Date.UTC(startYear, startMonth - 1 + i, 1));
    months.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/** JST 現在の "YYYY-MM"。 @graph-connects none */
export function jstCurrentYm(now: Date = new Date()): string {
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return `${String(jst.getUTCFullYear())}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * 月初日 "YYYY-MM-01" → その月の末日 "YYYY-MM-DD"。
 *
 * pt 明細 API (`/members/:email/develop-points`) へ「その月だけ」の範囲を渡す用途。
 * 当月pt 明細モーダルと当月pt 内訳カードが同じ範囲を組み立てるため、ここを唯一の実装にする
 * (月末日を 28/30/31 で取り違えると、月末完了分だけが静かに落ちる)。
 * @graph-connects none
 */
export function monthEndOf(monthStart: string): string {
  const year = parseInt(monthStart.slice(0, 4), 10);
  const month = parseInt(monthStart.slice(5, 7), 10);
  // Date(y, m, 0) は前月末日 = 対象月の末日 (month は 1-12 のまま渡す)
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${monthStart.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * availableQuarters (Qまとめシート名) を月初日 "YYYY-MM-01" の降順リストへ展開する。
 * currentYm ("YYYY-MM") 以前の月のみ含め (未来月は除外)、当月は必ず含める。
 * @graph-connects none
 */
export function pastMonthOptionsOf(availableQuarters: string[], currentYm: string): string[] {
  const set = new Set<string>();
  for (const title of availableQuarters) {
    const q = parseQuarterTitle(title);
    if (q === null) continue;
    for (const ym of quarterMonths(q)) {
      if (ym <= currentYm) set.add(`${ym}-01`);
    }
  }
  set.add(`${currentYm}-01`);
  return [...set].sort().reverse();
}

/**
 * 月初日 "YYYY-MM-01" が属する Q の availableQuarters 内タイトルを返す。
 * 当四半期は undefined (API のデフォルト解決に委ねる = 当四半期クエリと dedupe)。
 * availableQuarters に該当 Q が無ければ null。
 * @graph-connects none
 */
export function quarterParamOfMonth(
  monthFirstDay: string,
  currentYm: string,
  availableQuarters: string[],
): string | null | undefined {
  const fq = fiscalQuarterOf(
    parseInt(monthFirstDay.slice(0, 4), 10),
    parseInt(monthFirstDay.slice(5, 7), 10),
  );
  const curFq = fiscalQuarterOf(
    parseInt(currentYm.slice(0, 4), 10),
    parseInt(currentYm.slice(5, 7), 10),
  );
  if (fq.fy === curFq.fy && fq.q === curFq.q) return undefined;
  const title = availableQuarters.find((t) => {
    const p = parseQuarterTitle(t);
    return p !== null && p.fy === fq.fy && p.q === fq.q;
  });
  return title ?? null;
}
